import React, {
    useEffect, useState, useMemo, useCallback,
} from 'react';
import { faPlusCircle, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NSCard, NSSwitch } from 'bricks';
import { formatUSDCurrencyTwoDigits } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds } from 'constants/proForma';
import { ITenant, IUnitMix, TUnit } from 'views/ProFormaWizard/components/ProFormaWizardStepThree/types';
import { IExpense } from 'views/ProFormaWizard/BasicInformation/hooks/useExpenses';
import { useUnitManager } from 'views/ProFormaWizard/components/ProFormaWizardStepThree/hooks/useUnitManager';
import { useOtherIncomeManager } from 'views/ProFormaWizard/components/ProFormaWizardStepThree/hooks/useOtherIncomeManager';
import UnitMixOverviewCard from './components/UnitMixOverviewCard/UnitMixOverviewCard';
import TenantsOverviewCard from './components/TenantsOverviewCard/TenantsOverviewCard';
import OperatingInfo from './components/OperatingInfo/OperatingInfo';
import OtherIncomeCard from './components/OtherIncomeCard/OtherIncomeCard';
import Expenses from './components/Expenses/Expenses';
import './Operations.scss';
import { initialOperationsValues } from '../constants';
import { OperationsValues } from '../types';

interface OperationsProps {
    values: OperationsValues;
    dealSF: number;
    dealUnits: number;
    proFormaModelId: number;
    onUpdate: (values: any) => void;
}

const Operations: React.FC<OperationsProps> = ({
    values, dealSF, dealUnits, proFormaModelId, onUpdate,
}) => {
    const [expensesValues, setExpensesValues] = useState(values.expensesValues);

    const [operatingInfoValues, setOperatingInfoValues] = useState(values.operatingInfoValues);

    const [expensesDetail, setExpensesDetail] = useState<IExpense[]>(values.expensesDetail);
    const [showExpenseDetailToggle, setShowExpenseDetailToggle] = useState(true);
    const [showOtherIncomeToggle, setShowOtherIncomeToggle] = useState(true);
    const [unitArray, setUnitArray] = useState<TUnit[]>([]);

    const {
        handleDuplicateOrAdd,
        handleDelete,
        handleUpdate,
    } = useUnitManager({
        unitArray,
        setUnitArray,
        initialUnits: initialOperationsValues.unitArray,
    });

    const {
        incomeArray,
        handleAddOrDuplicateIncome,
        handleDeleteIncome,
        handleUpdateIncome,
    } = useOtherIncomeManager({
        initialIncomes: initialOperationsValues.incomeArray,
    });

    useEffect(() => {
        onUpdate?.({
            operatingInfoValues,
            unitArray,
            incomeArray,
            expensesValues,
            expensesDetail,
        });
    }, [operatingInfoValues, unitArray, incomeArray, expensesValues, expensesDetail]);

    const totalRentalIncome = useMemo(
        () => unitArray.reduce((sum, unit) => sum + (unit.totalRent !== undefined ? unit.totalRent : 0), 0), [unitArray],
    );
    const totalOtherIncome = useMemo(
        () => incomeArray.reduce((sum, unit) => sum + (unit.amountPerYear !== undefined ? unit.amountPerYear : 0), 0), [incomeArray],
    );

    const handleInputChange = useCallback((key: string, value: number | string) => {
        setExpensesValues((prev: any) => ({ ...prev, [key]: value }));
    }, []);

    const handleDetailChange = useCallback((updated: IExpense[]) => setExpensesDetail(updated), []);
    const handleOperatingInfoChange = useCallback((key: any, value: any) => setOperatingInfoValues((prev: any) => ({ ...prev, [key]: value })), []);

    const renderCards = () => unitArray.map(unit => (
        <div className="w-100 px-1 pt-1" key={unit.id}>
            {proFormaModelId === ProFormaModelIds.APARTMENT ? (
                <UnitMixOverviewCard
                    values={unit as IUnitMix}
                    onDuplicate={() => handleDuplicateOrAdd(unit.id)}
                    onDelete={() => handleDelete(unit.id)}
                    onUpdate={handleUpdate}
                />
            ) : (
                <TenantsOverviewCard
                    values={unit as ITenant}
                    onDuplicate={() => handleDuplicateOrAdd(unit.id)}
                    onDelete={() => handleDelete(unit.id)}
                    onUpdate={handleUpdate}
                    count={unitArray.length}
                />
            )}
        </div>
    ));

    return (
        <div className="Operations p-3">
            <OperatingInfo onChange={handleOperatingInfoChange} values={operatingInfoValues} />

            <div className="ProFormaWizardStepOne__separator" />
            <div className="ProFormaWizardStepOne__title">{proFormaModelId === ProFormaModelIds.APARTMENT ? 'Unit Mix' : 'Tenants'}</div>

            <NSCard className="mt-2 p-2" title="Unit Mix">
                {renderCards()}
                <div className="d-flex flex-column px-1">
                    <NSCard
                        onClick={() => handleDuplicateOrAdd()}
                        className={`cursor--pointer text-white align-items-center
                        justify-content-center mb-2 flex-grow-1 ${proFormaModelId === ProFormaModelIds.APARTMENT
            ? 'ProFormaWizardStepThree__addApartmentCard'
            : 'ProFormaWizardStepThree__addOtherCard'
        }`}
                    >
                        <FontAwesomeIcon icon={faPlusCircle} size="lg" className="pb-1" />
                        <div>Add unit type</div>
                    </NSCard>
                </div>
                <NSCard className="NSCard--level-4">
                    <div className="d-flex justify-content-between text-white font-weight-bold p-2">
                        Total annual rental income
                        <div>{formatUSDCurrencyTwoDigits(totalRentalIncome)}</div>
                    </div>
                </NSCard>
            </NSCard>

            <NSCard className="mt-2 p-2" title="Other Income">
                <div className="d-flex justify-content-between">
                    <div>
                        <div className="ProFormaWizardStepThree__unitTitle">Other income</div>
                        <div className="ProFormaWizardStepOne__subtitle">Add additional income sources (e.g. RUBS, Parking)</div>
                    </div>
                    <div className="ProFormaWizardStepOne__subtitle align-self-center">
                        <NSSwitch
                            id="calculateToggle"
                            name="calculateToggle"
                            checked={showOtherIncomeToggle}
                            onChange={() => setShowOtherIncomeToggle(prev => !prev)}
                            label=""
                            containerClassName="text-dark"
                        />
                    </div>
                </div>
                <RenderIf isTrue={showOtherIncomeToggle}>
                    <div className="mt-2">
                        {incomeArray.map(income => (
                            <OtherIncomeCard
                                key={income.id}
                                values={income}
                                onUpdate={handleUpdateIncome}
                                onDelete={() => handleDeleteIncome(income.id)}
                            />
                        ))}
                        <NSCard
                            onClick={() => handleAddOrDuplicateIncome()}
                            className="
                            ProFormaWizardStepThree__addOtherIncomeCard
                            cursor--pointer text-white align-items-center justify-content-center mb-2 flex-grow-1"
                        >
                            <div className="py-3">
                                <FontAwesomeIcon icon={faPlus} className="pr-2" />
                                Add other income
                            </div>
                        </NSCard>
                    </div>
                    <NSCard className="NSCard--level-4">
                        <div className="d-flex justify-content-between text-white font-weight-bold p-2">
                            Total other income:
                            <div>{formatUSDCurrencyTwoDigits(totalOtherIncome)}</div>
                        </div>
                    </NSCard>
                </RenderIf>
            </NSCard>

            <div className="ProFormaWizardStepOne__separator" />
            <Expenses
                values={expensesValues}
                proFormaModelId={proFormaModelId}
                onChange={handleInputChange}
                onDetailChange={handleDetailChange}
                showExpenseDetail={showExpenseDetailToggle}
                setShowExpenseDetail={setShowExpenseDetailToggle}
                dealSF={dealSF}
                dealUnits={dealUnits}
                initialExpenses={expensesDetail}
            />
        </div>
    );
};

export default Operations;
